import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import GenericTable, {
  TableColumn,
} from '@/components/school-management/GenericTable/GenericTable';
import { SchoolDetails } from '@/admin/config/listConfig';
import {
  TeacherReportItem,
  TeachersReportQueryParams,
  TeachersReportSortByQueryParams,
} from '@/school-management/types/response';
import { dateFiltersToTeachersLoginTimes } from '@/school-management/utils/filterUtils';
import classNames from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import classnames from 'classnames';
import DropdownMenu from '@/components/school-management/DropdownMenu/DropdownMenu';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@/admin/config/routes';

import { Transition } from 'react-transition-group';
import AppBackdrop from '@/components/AppBackdrop';
import AppModal from '@/components/AppModal';
import AppModalWithShow from '@/components/AppModal/AppModal';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import SVG from 'react-inlinesvg';
import closeIcon from '@/admin/assets/svg/close.svg';
import TheButton from '@/components/school-management/TheButton/TheButton';
import copyIcon from '@/admin/assets/svg/copy.svg';
import { adminManagementService } from '@/admin/services/adminManagement';
import { teacherService } from '@/school-management/services/teacher';
import { UserData } from '@/school-management/types/user-data';
import ClassModalsManager from '@/components/school-management/SchoolDetailsPage/ClassModalsManager';
import DeleteTeacherModal from '@/admin/components/DeleteTeacherModal/DeleteTeacherModal';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { formatDateShort } from '@/utils/formatters';
import zeroStateStyles from './zeroState.module.scss';
import Button from '@/components/school-management/Button/Button';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import config from '@/config';
import { FilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import { TeacherAutocompleteItem } from '@/school-management/types/response/autocomplete';
import processing, { XlsxData, XlsxTable } from '@/school-management/utils/processing';
const PAGE_SIZE = config.teachersPageSize;

interface TeachersProps {
  school: SchoolDetails;
  teachers: TeacherReportItem[];
  setTeachers: React.Dispatch<React.SetStateAction<TeacherReportItem[]>>;
  addingTeacher: boolean;
  setAddingTeacher: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateNewTeacher: () => void | Promise<void>;
  editTeacherModalOpen: boolean;
  setEditTeacherModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editTeacherData: any;
  setEditTeacherData: React.Dispatch<React.SetStateAction<any>>;
  inviteModalOpen: boolean;
  setInviteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGrade: { label: string; value: string };
  setSelectedGrade: React.Dispatch<React.SetStateAction<{ label: string; value: string }>>;
  gradeOptions: { label: string; value: string }[];
  setTeacherClassesMode: React.Dispatch<React.SetStateAction<boolean>>;
  setClassFilters: React.Dispatch<React.SetStateAction<Record<string, FilterValue>>>;
  teacherFilters?: Record<string, any>;
  hasAnyFilter?: boolean;
  setShowTeachersStatistics: React.Dispatch<React.SetStateAction<boolean>>;
  setShowTeachersStatsSection: React.Dispatch<React.SetStateAction<boolean>>;
  refreshSchoolDetails?: () => void;
}

const Teachers: React.FC<TeachersProps> = ({
  school,
  teachers,
  setTeachers,
  addingTeacher,
  setAddingTeacher,
  onCreateNewTeacher,
  editTeacherModalOpen,
  setEditTeacherModalOpen,
  editTeacherData,
  setEditTeacherData,
  inviteModalOpen,
  setInviteModalOpen,
  selectedGrade,
  setSelectedGrade,
  gradeOptions,
  setTeacherClassesMode,
  setClassFilters,
  teacherFilters,
  hasAnyFilter,
  setShowTeachersStatistics,
  setShowTeachersStatsSection,
  refreshSchoolDetails,
}) => {
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);
  const [inviteLink, setInviteLink] = useState<string>('');
  const [isLoadingInviteLink, setIsLoadingInviteLink] = useState<boolean>(false);
  const [showAddTeachersModal, setShowAddTeachersModal] = useState(false);
  const [addTeachersToNewGroup, setAddTeachersToNewGroup] = useState(false);
  const [showCsvTeachersCreateError, setShowCsvTeachersCreateError] = useState<boolean>(false);
  const [showDeleteTeacherModal, setShowDeleteTeacherModal] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<{ id: string; name: string } | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('info');
  const [showLinkCopiedModal, setShowLinkCopiedModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [teachersError, setTeachersError] = useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = useState<{
    totalPages: number;
    totalCount: number;
  }>({
    totalPages: 1,
    totalCount: 0,
  });
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const hasActiveSearch = searchQuery.trim().length > 0;

  const schoolId = user?.metadata?.organizationDetails?.organizationId;

  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tRef = useRef(t);
  tRef.current = t;

  const buildTeacherQueryParams = useCallback(
    (page: number): TeachersReportQueryParams => {
      const tf = (teacherFilters || {}) as Record<string, any>;
      const queryParams: TeachersReportQueryParams = {
        page,
        pageSize: PAGE_SIZE,
      };

      if (sortBy) {
        queryParams.sortBy = sortBy as TeachersReportSortByQueryParams;
        queryParams.sortDirection = sortDirection;
      }

      const normalizedSearchQuery = searchQuery.trim();
      if (normalizedSearchQuery) {
        queryParams.search = normalizedSearchQuery;
      }

      if (tf.teachers) {
        const teacherIds = (tf.teachers as TeacherAutocompleteItem[]).map(t => t.id);
        if (teacherIds.length > 0) {
          queryParams.teacherIds = teacherIds;
        }
      }

      const loginTimes = dateFiltersToTeachersLoginTimes(tf.firstLogin, tf.lastLogin);
      if (Object.keys(loginTimes).length > 0) queryParams.loginTimes = loginTimes;

      return queryParams;
    },
    [teacherFilters, sortBy, sortDirection, searchQuery]
  );

  // Fetch teachers with pagination and filters (POST search)
  const fetchTeachers = useCallback(
    async (page: number) => {
      if (!schoolId) return;

      setIsLoadingTeachers(true);
      setTeachersError(null);
      try {
        const queryParams = buildTeacherQueryParams(page);
        const response = await teacherService.getAllTeachers(schoolId, queryParams);
        setTeachers(response.data.items);
        setPaginationInfo({
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
        });
      } catch (error: any) {
        setTeachersError(
          error?.message || tRef.current('schoolDetailsPage.teachers.failedToFetch')
        );
        setTeachers([]);
        setPaginationInfo({ totalPages: 0, totalCount: 0 });
      } finally {
        setIsLoadingTeachers(false);
        setIsFirstLoading(false);
      }
    },
    [schoolId, buildTeacherQueryParams, setTeachers]
  );

  const lastFetchedKeyRef = useRef<string>('');
  React.useEffect(() => {
    if (!schoolId) return;
    const key = `${currentPage}-${schoolId}-${sortBy}-${sortDirection}-${searchQuery || ''}-${JSON.stringify(teacherFilters ?? {})}`;
    if (lastFetchedKeyRef.current === key) return;
    lastFetchedKeyRef.current = key;
    fetchTeachers(currentPage);
  }, [currentPage, schoolId, teacherFilters, fetchTeachers, sortBy, sortDirection, searchQuery]);

  const prevFiltersJsonRef = useRef<string | null>(null);
  React.useEffect(() => {
    const filtersJson = JSON.stringify(teacherFilters ?? {});
    if (prevFiltersJsonRef.current !== null && prevFiltersJsonRef.current !== filtersJson) {
      setCurrentPage(1);
    }
    prevFiltersJsonRef.current = filtersJson;
  }, [teacherFilters]);

  useEffect(() => {
    setShowTeachersStatsSection(
      isFirstLoading ||
        isLoadingTeachers ||
        (teachers && teachers.length > 0) ||
        !!hasAnyFilter ||
        hasActiveSearch
    );
  }, [
    setShowTeachersStatsSection,
    teachers,
    hasAnyFilter,
    isLoadingTeachers,
    isFirstLoading,
    hasActiveSearch,
  ]);

  useEffect(() => {
    if (isFirstLoading) {
      setShowTeachersStatistics(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowTeachersStatistics(
        isLoadingTeachers || (teachers && teachers.length > 0) || !!hasAnyFilter || hasActiveSearch
      );
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [
    setShowTeachersStatistics,
    teachers,
    hasAnyFilter,
    isLoadingTeachers,
    isFirstLoading,
    hasActiveSearch,
  ]);

  const onClassCodeCopy = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setAddingTeacher(false);
      setShowLinkCopiedModal(true);
    } else {
      console.log('No invite link');
      setAddingTeacher(false);
    }
  };

  const onClose = () => {
    setAddingTeacher(false);
  };

  const onAddManually = () => {
    setAddingTeacher(false);
    setShowAddTeachersModal(true);
  };

  useEffect(() => {
    if (addingTeacher) {
      setIsLoadingInviteLink(true);
      setInviteLink('');

      const timeoutRef = { current: null as any | null };

      const fetchInviteLink = async () => {
        const startTime = Date.now();
        try {
          if (!schoolId) return;
          const response = await adminManagementService.getTeachersInviteLink(schoolId);
          if (response?.data?.data?.invitationLink) {
            setInviteLink(response?.data?.data?.invitationLink);
          } else {
            console.error('Failed to get invite link');
          }
        } catch (error) {
          console.error('Error fetching invite link:', error);
        } finally {
          const elapsedTime = Date.now() - startTime;
          const minDelay = 500;
          const remainingTime = Math.max(0, minDelay - elapsedTime);

          timeoutRef.current = setTimeout(() => {
            setIsLoadingInviteLink(false);
          }, remainingTime);
        }
      };

      fetchInviteLink();

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } else {
      const resetTimeout = setTimeout(() => {
        setIsLoadingInviteLink(false);
        setInviteLink('');
      }, 400);

      return () => {
        clearTimeout(resetTimeout);
      };
    }
  }, [addingTeacher, schoolId]);

  const classCodeModal = (
    <Transition
      in={addingTeacher}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop className={classnames(classNames.backdrop, `backdrop-${state}`)} />
            <AppModal>
              <div className={classnames(classNames.classCodeModal, `fade-${state}`)}>
                <button
                  onClick={onClose}
                  className={classNames.close}
                  aria-label={t('close').toString()}
                >
                  <SVG src={closeIcon} />
                </button>
                <h2>{t('schoolDetailsPage.teachers.inviteYourTeachers')}</h2>
                <h3 className={classNames.description} style={{ margin: 0 }}>
                  {t('schoolDetailsPage.teachers.inviteLinkDescription')}
                </h3>
                <div className={classNames.inviteLinkContainer}>
                  <SVG src={copyIcon} width={28} height={28} />
                  <span className={classNames.classCode}>
                    {isLoadingInviteLink ? (
                      <AppLoaderCircle />
                    ) : (
                      <span className={classNames.inviteLink} rel='noopener noreferrer'>
                        {inviteLink}
                      </span>
                    )}
                  </span>
                </div>
                <div className={classNames.buttonsContainer}>
                  {!isLoadingInviteLink && (
                    <TheButton
                      className={classNames.copyBtn}
                      text={t('schoolDetailsPage.teachers.copyLink')}
                      emitOnClick={onClassCodeCopy}
                      shouldRecolor={false}
                      disabled={!inviteLink}
                    />
                  )}
                  <TheButton
                    className={classNames.manuallyBtn}
                    text={t('schoolDetailsPage.teachers.addManually')}
                    plain
                    emitOnClick={onAddManually}
                    shouldRecolor={false}
                  />
                </div>
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );

  const onCsvTeachersCreate = async (users: UserData[]) => {
    if (users && adminManagementService.createCsvTeachers) {
      const response = await adminManagementService.createCsvTeachers(null, undefined, users);
      return response;
    }

    return [];
  };

  const handleDeleteTeacher = useCallback(
    async (teacherId: string) => {
      try {
        if (schoolId) {
          setTeachers(prevTeachers => prevTeachers.filter(teacher => teacher.userId !== teacherId));
          setShowDeleteTeacherModal(false);
          setTimeout(() => {
            setTeacherToDelete(null);
          }, 400);
          setNotifMessage(t('dynamicForm.notification.teacherDeleted') || '');
          setNotifType('success');
          setNotifOpen(true);
          refreshSchoolDetails?.();
        }
      } catch (error) {
        console.error('Error deleting teacher:', error);
        setNotifMessage(t('dynamicForm.notification.generalError') || '');
        setNotifType('error');
        setNotifOpen(true);
      }
    },
    [schoolId, setTeachers, t, refreshSchoolDetails]
  );

  const handleDeleteClick = (teacherId: string, teacherName: string) => {
    setTeacherToDelete({ id: teacherId, name: teacherName });
    setShowDeleteTeacherModal(true);
  };

  const handleCancelDelete = useCallback(() => {
    setShowDeleteTeacherModal(false);
    setTimeout(() => {
      setTeacherToDelete(null);
    }, 400);
  }, []);

  const handleTeachersRefetch = useCallback(() => {
    fetchTeachers(1);
    setCurrentPage(1);
  }, [fetchTeachers]);

  const classModalsManager = (
    <ClassModalsManager
      showUploadModal={showAddTeachersModal}
      groupId={schoolId || ''}
      setShowUploadModal={setShowAddTeachersModal}
      addUsersToNewEntity={addTeachersToNewGroup}
      setAddUsersToNewEntity={setAddTeachersToNewGroup}
      onUsersCreate={onCsvTeachersCreate}
      setShowUsersCreateError={setShowCsvTeachersCreateError}
      isTeacher={true}
      dealId={user?.metadata?.organizationDetails?.dealId}
      onTeachersRefetch={handleTeachersRefetch}
      refreshSchoolDetails={refreshSchoolDetails}
    />
  );

  const rowDropdownMenu = (row: TeacherReportItem) => {
    const items = [
      {
        label: t('schoolDetailsPage.classes.actions.edit'),
        action: 'edit',
        onClick: (id: string) => {
          if (!schoolId) return;
          navigate(ROUTES.EDIT_TEACHER(schoolId, id), {
            state: { teacher: row },
          });
        },
      },
      {
        label: t('schoolDetailsPage.classes.actions.delete'),
        action: 'delete',
        danger: true,
        onClick: (id: string) => {
          handleDeleteClick(id, `${row.firstName} ${row.lastName}`);
        },
      },
    ];

    if (row.numberOfClasses) {
      items.splice(1, 0, {
        label: t('schoolDetailsPage.classes.viewClasses'),
        action: 'view classes',
        onClick: (id: string) => {
          setClassFilters({
            groupName: [],
            teacher: [
              {
                id: id,
                firstName: row.firstName,
                lastName: row.lastName,
                email: row.email || '',
              },
            ],
            grade: [],
          });
          setTeacherClassesMode(true);
          setSearchParams({ tab: 'classes' });
        },
      });
    }

    return items;
  };

  const columns: TableColumn<TeacherReportItem>[] = [
    {
      key: 'name',
      label: t('genericTable.teachers.name'),
      width: '2fr',
      sortable: true,
      render: (value: any, row: TeacherReportItem) => (
        <span className={classNames.teacherName}>{row.name}</span>
      ),
      xlsxRender: (value, row) => row.name,
    },
    {
      key: 'numberOfClasses',
      label: t('genericTable.teachers.numClasses'),
      width: '1fr',
      sortable: true,
      render: (value: any, row: TeacherReportItem) => <span>{row.numberOfClasses}</span>,
      xlsxRender: (value, row) => row.numberOfClasses,
    },
    {
      key: 'numberOfStudents',
      label: t('genericTable.teachers.numStudents'),
      width: '1fr',
      sortable: true,
      render: (value: any, row: TeacherReportItem) => (
        <span className={classNames.teacherName}>{row.numberOfStudents}</span>
      ),
      xlsxRender: (value, row) => row.numberOfStudents,
    },
    {
      key: 'numberOfActiveStudents',
      label: t('genericTable.teachers.activeStudents'),
      width: '1fr',
      sortable: true,
      render: (value: any, row: TeacherReportItem) => {
        const totalStudents = row.numberOfStudents || 0;
        const activeStudents = row.numberOfActiveStudents || 0;
        const activePercent =
          totalStudents > 0 ? Math.round((activeStudents / totalStudents) * 100) : 0;
        return (
          <>
            <span className={classNames.teacherName}>{activeStudents}</span>
            <br></br>
            <span className={classNames.teacherName}>({activePercent}%)</span>
          </>
        );
      },
      xlsxRender: (value, row) => {
        const totalStudents = row.numberOfStudents || 0;
        const activeStudents = row.numberOfActiveStudents || 0;
        const activePercent =
          totalStudents > 0 ? Math.round((activeStudents / totalStudents) * 100) : 0;
        return `${activeStudents}(${activePercent}%)`;
      },
    },
    {
      key: 'firstLogin',
      label: t('genericTable.teachers.firstLogin'),
      width: '1fr',
      sortable: true,
      render: (value: any, row: TeacherReportItem) => {
        if (row.firstLogin) {
          let firstLogin = row.firstLogin;
          if (firstLogin && !firstLogin.toUpperCase().endsWith('Z')) {
            firstLogin = `${firstLogin}Z`;
          }
          return <span className={classNames.teacherName}>{formatDateShort(firstLogin)}</span>;
        }
        // If lastLogin exists but firstLogin doesn't, this is a legacy user created before tracking
        if (row.lastLogin && !row.firstLogin) {
          return <span className={classNames.teacherName}>N/A</span>;
        }
        // If neither exists, this is a new user who hasn't logged in yet
        return <span className={classNames.teacherName}></span>;
      },
      xlsxRender: (value, row) => {
        if (row.firstLogin) {
          let firstLogin = row.firstLogin;
          if (firstLogin && !firstLogin.toUpperCase().endsWith('Z')) {
            firstLogin = `${firstLogin}Z`;
          }
          return formatDateShort(firstLogin);
        }
        if (row.lastLogin && !row.firstLogin) {
          return 'N/A';
        }
        return '';
      },
    },
    {
      key: 'lastLogin',
      label: t('genericTable.teachers.lastLogin'),
      width: '1fr',
      sortable: true,
      render: (value: any, row: TeacherReportItem) => {
        let lastLogin = row.lastLogin || '';

        if (lastLogin && !lastLogin.toUpperCase().endsWith('Z')) {
          lastLogin = `${lastLogin}Z`;
        }

        if (lastLogin) {
          lastLogin = formatDateShort(lastLogin);
        }

        return <span className={classNames.teacherName}>{lastLogin}</span>;
      },
      xlsxRender: (value, row) => {
        let lastLogin = row.lastLogin || '';

        if (lastLogin && !lastLogin.toUpperCase().endsWith('Z')) {
          lastLogin = `${lastLogin}Z`;
        }

        if (lastLogin) {
          lastLogin = formatDateShort(lastLogin);
        }

        return lastLogin;
      },
    },
    {
      key: 'actions',
      label: '',
      width: '55px',
      render: (_: any, row: TeacherReportItem) => (
        <DropdownMenu id={row.userId} items={rowDropdownMenu(row)} hasStudents={true} />
      ),
    },
  ];

  const tableName = useMemo(() => {
    return t(`schoolDetailsPage.teachers.title`) || '';
  }, [t]);

  const onDownload = useCallback(async () => {
    console.log('Clicked to download teachers CSV');

    let teachersData: TeacherReportItem[] = [];

    if (schoolId) {
      try {
        const pageQueryParam = buildTeacherQueryParams(1);
        pageQueryParam.page = 0;
        let hasNextPage: boolean = true;

        while (hasNextPage) {
          pageQueryParam.page++;
          const response = await teacherService.getAllTeachers(schoolId, pageQueryParam);

          if (response?.data?.items && response.data.items.length > 0) {
            teachersData = [...teachersData, ...response.data.items];
          }

          hasNextPage = !!response?.data?.hasNextPage;
        }
      } catch (er) {}
    }

    const xlsxColumns = columns.filter(c => !!c.xlsxRender);
    const headerRow = xlsxColumns.map(c => c.xlsxLabel || c.label);

    const xlsxData: XlsxData = {
      fileName: tableName,
      tables: [],
    };

    const skillTable: XlsxTable = {
      name: tableName,
      headerRow: [...headerRow],
      data: [],
    };
    teachersData.forEach(l => {
      const row: any = {};
      xlsxColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((l as any)[col.key], l);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      skillTable.data.push(row);
    });
    xlsxData.tables.push(skillTable);

    processing.createXlsx(xlsxData);
  }, [schoolId, buildTeacherQueryParams, columns, tableName]);

  const isEmpty = !isLoadingTeachers && (teachers || []).length === 0;

  return (
    <>
      {classCodeModal}
      {classModalsManager}
      <DeleteTeacherModal
        show={showDeleteTeacherModal}
        schoolId={schoolId}
        teacherId={teacherToDelete?.id || ''}
        teacherName={teacherToDelete?.name || ''}
        onDelete={handleDeleteTeacher}
        onCancel={handleCancelDelete}
      />
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => setNotifOpen(false)}
      />
      <AppModalWithShow show={showLinkCopiedModal} type='fade'>
        <div className={classnames(classNames.modal, classNames.copyModal)}>
          <h2>{t('schoolDetailsPage.teachers.linkCopied')}</h2>
          <span className={classNames.description}>
            {t('schoolDetailsPage.teachers.linkCopiedMessage')}
          </span>
          <TheButton
            className={classNames.btn}
            text={t('ok')}
            shouldRecolor={false}
            emitOnClick={() => setShowLinkCopiedModal(false)}
          />
        </div>
      </AppModalWithShow>
      {teachersError && (
        <div>{t('schoolDetailsPage.teachers.errorLoading', { error: teachersError })}</div>
      )}
      {!teachersError && (
        <div className={`${classNames.teachersMode} ${isEmpty ? classNames.emptyStateMode : ''}`}>
          <div
            className={`${classNames.classesDetailsCard} ${isEmpty ? classNames.emptyStateCard : ''}`}
          >
            <GenericTable
              columns={columns}
              data={teachers}
              rowKey={t => (t as TeacherReportItem).userId}
              showSearch={true}
              showHeaderInEmptyState={
                isFirstLoading || isLoadingTeachers || hasAnyFilter || hasActiveSearch
              }
              searchPlaceholder={
                t('schoolDetailsPage.teachers.searchPlaceholder') || 'Search teachers'
              }
              onSearchQueryChange={query => {
                setSearchQuery(query);

                if ((query || '').trim()) {
                  setSortBy('');
                } else if (!sortBy) {
                  setSortBy('name');
                  setSortDirection('asc');
                }

                setCurrentPage(1);
              }}
              variant='compact'
              className={classNames.classesTable}
              pageSize={PAGE_SIZE}
              showSortArrows={true}
              title={tableName}
              onDownload={onDownload}
              serverSideDownload={true}
              downloadFileName={tableName}
              hasData={hasAnyFilter || teachers.length > 0}
              emptyStateTitle={
                isLoadingTeachers || hasAnyFilter || hasActiveSearch
                  ? ' '
                  : t('schoolDetailsPage.teachers.zeroState.title') || ''
              }
              emptyStateDescription={
                isLoadingTeachers
                  ? ' '
                  : hasActiveSearch
                    ? t('genericTable.noResultsForSearchQuery', {
                        searchQuery: searchQuery,
                      }) || ''
                    : hasAnyFilter
                      ? t('schoolDetailsPage.teachers.zeroState.filterTitle') || ''
                      : t('schoolDetailsPage.teachers.zeroState.description') || ''
              }
              emptyStateSubDescription={
                hasAnyFilter && !isLoadingTeachers
                  ? t('schoolDetailsPage.teachers.zeroState.filterDescription') || ''
                  : undefined
              }
              emptyStateAction={
                isFirstLoading ||
                isLoadingTeachers ||
                hasAnyFilter ||
                hasActiveSearch ? undefined : (
                  <Button
                    onClick={() => onCreateNewTeacher()}
                    className={zeroStateStyles.zeroStateButton}
                  >
                    {t('schoolDetailsPage.teachers.newTeacher')}
                  </Button>
                )
              }
              showLoader={isFirstLoading || isLoadingTeachers}
              serverSidePagination={true}
              totalPages={paginationInfo.totalPages}
              totalCount={paginationInfo.totalCount}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              serverSideSorting={true}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={(newSortBy: string | null, newSortDirection: 'asc' | 'desc' | null) => {
                if (newSortBy && newSortDirection) {
                  setSortBy(newSortBy);
                  setSortDirection(newSortDirection);
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Teachers;
